import React from 'react';
import { JURISDICTIONS } from '../mock';
import { ShieldCheck } from 'lucide-react';
import ClosingCTA from '../components/ClosingCTA';
import SEO from '../components/SEO';

export default function Trust() {
  return (
    <div>
      <SEO
        title="Trust & governance"
        description="Where DigitalWorks operates, how PII stays behind the firewall, and the release gates every workstream passes through before anything reaches production."
        canonical="https://digitalworks.co/trust"
      />
      <section className="pt-16 pb-10">
        <div className="dwx">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">Trust</div>
          <h1 className="serif-tight text-[54px] md:text-[92px] leading-[0.94] mt-4 max-w-4xl">Trust, security<br />and governance.</h1>
          <p className="mt-10 max-w-3xl text-[17px] leading-relaxed text-[#1A1A1A]/80">Everything a security reviewer, a procurement team or a data protection officer needs, in one place. If something you need is missing, ask us and we will publish it.</p>
        </div>
      </section>

      {/* The rule */}
      <section className="py-16">
        <div className="dwx">
          <div className="rounded-[24px] bg-[#22A94F]/10 border border-[#22A94F]/30 p-10">
            <div className="flex items-start gap-4">
              <ShieldCheck size={28} className="text-[#178838] flex-shrink-0 mt-1" />
              <div>
                <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#178838]">The rule that governs everything</div>
                <h2 className="serif-tight text-[36px] md:text-[48px] leading-[1.02] mt-3 max-w-3xl">Agents propose and prepare. Two different people review and release.</h2>
                <p className="mt-4 text-[15px] leading-relaxed text-[#1A1A1A]/80 max-w-3xl">Nothing reaches your production environment on an agent’s authority.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="py-16">
        <div className="dwx grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ['Identity & access', 'Client identity federated via Keycloak/OIDC. Agents run as service principals with pre-defined access. Production access, when required, is time-limited by the Auth Broker Service and logged.'],
            ['Agent containment', 'Agents work in non-production environments (test, epic branches). Production is never entered directly. Shadow data stands in for real data during simulation and testing.'],
            ['Action risk gates', 'Four tiers, four axes, hard scope guards. Reading a report is not deleting a table — and the classification proves it before anything runs.'],
            ['Execution ledger', 'Proposal Log records what the agent intended. Execution Ledger records what happened. The delta is the explainability artefact. Approval ledger persists to WORM storage.'],
            ['Privacy & erasure', 'Pseudonym layer keeps PII in a separate erasable store. Log tokens replace PII where it must appear. A single erasure operation removes both mappings.'],
            ['Data residency', 'Cloud-agnostic. Same control model deploys in EU, UK, UAE, KSA, India, Singapore, Australia. Deployment fits your jurisdiction, not the other way around.'],
            ['IP ownership', 'You own the code and artefacts produced under your engagement. NeuralWorks itself remains DigitalWorks IP.'],
            ['Model hosting', 'Frontier models run in accounts and regions your regime permits. Nothing that leaves your infrastructure is retained by third-party providers for training.'],
          ].map(([t, b]) => (
            <div key={t} className="rounded-[20px] bg-[#F4F2EC] p-8 border border-[#1A1A1A]/10">
              <div className="serif text-[24px]">{t}</div>
              <p className="mt-3 text-[14px] leading-relaxed text-[#1A1A1A]/80">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Jurisdictions */}
      <section className="py-16 bg-[#F4F2EC]">
        <div className="dwx">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">Data residency</div>
          <h2 className="serif-tight text-[38px] md:text-[52px] leading-[0.98] mt-3">Regimes we have deployed under.</h2>
          <div className="mt-10 rounded-[20px] border border-[#1A1A1A]/10 overflow-hidden bg-[#FBFAF6]">
            <table className="w-full text-[14px]">
              <thead className="bg-[#E8E4D9] text-left text-[#1A1A1A]/70">
                <tr>
                  <th className="p-4 mono text-[11px] tracking-[0.22em] uppercase">Region</th>
                  <th className="p-4 mono text-[11px] tracking-[0.22em] uppercase">Regime</th>
                  <th className="p-4 mono text-[11px] tracking-[0.22em] uppercase">Deployment</th>
                </tr>
              </thead>
              <tbody>
                {JURISDICTIONS.map((j) => (
                  <tr key={j.region} className="border-t border-[#1A1A1A]/10">
                    <td className="p-4 font-medium">{j.region}</td>
                    <td className="p-4 text-[#1A1A1A]/75">{j.regime}</td>
                    <td className="p-4 text-[#1A1A1A]/75">{j.deployment}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <ClosingCTA />
    </div>
  );
}
